import React, { useCallback, useEffect, useRef, useState } from 'react';

import debouncePromise from 'debounce-promise';
import { useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { Label } from '@atlaskit/form';
import ErrorIcon from '@atlaskit/icon/core/status-error';
import Link from '@atlaskit/link';
import Modal, { ModalBody, ModalFooter, ModalHeader, ModalTitle } from '@atlaskit/modal-dialog';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import Select, { AsyncSelect, type InputActionMeta, type SelectProps } from '@atlaskit/select';
import { token } from '@atlaskit/tokens';
import { setSessionItem } from '@atlassian/search-dialog/session-storage-utils';

import { useAIAnswerContext } from '../../../../ai-answer-dialog/ai-answer-context';
import { SEARCH_AI_ANSWER_CACHE_KEY } from '../../../../constants';
import { onCuratedDefinitionUpdateButtonClicked } from '../../analytics';
import { updateDefinitionErrorPayload } from '../../error-analytics';
import {
	type CuratedDefinitionScopeConfigType,
	type CuratedDefinitionSourceType,
	EditedDefinitionScope,
	type RestrictionOptionType,
} from '../types';

import { MarkdownEditor } from './markdown-editor';
import { messages } from './messages';
import { RadialProgressBar } from './radial-progress-bar';
import { restrictionOptionFormatter } from './restriction-option-formatter';
import { SourceOption } from './source-option-formatter';
import { useCreateDefinitionSupplier } from './useCreateDefinitionSupplier';
import { useSearchSourceSupplier } from './useSearchSourceSupplier';

type EditDefinitionModalProps = {
	curatedDefinitionScopeConfig: CuratedDefinitionScopeConfigType;
	closeEditModal: () => void;
	openFeedbackOptionsModal?: () => void;
	isCreateDefinition?: boolean;
	onSaveEditModal?: () => void;
};

const MAX_DEFINITION_LENGTH = 500;

const editDefintionModalStyles = cssMap({
	editDefinitionTextboxLabelContainer: {
		paddingBlockEnd: token('space.050'),
	},
	editDefinitionTextboxErrorMessageContainer: {
		paddingBlockStart: token('space.050'),
	},
	radialProgressBarContainer: {
		paddingBlockEnd: token('space.025'),
	},
	restrictionSelectSublineContainer: {
		paddingBlockStart: token('space.050'),
	},
});

export const EditDefinitionModal = ({
	curatedDefinitionScopeConfig,
	closeEditModal,
	openFeedbackOptionsModal,
	isCreateDefinition,
	onSaveEditModal,
}: EditDefinitionModalProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { fireAnalyticsEvent, commonAttributes, setData, answerEditor, additionalQueryContext } =
		useAIAnswerContext();
	const { answerString, query: keyPhrase } = commonAttributes;

	const [editedDefinition, setEditedDefinition] = useState(answerString);
	const [charCount, setCharCount] = useState<number>(0);

	const emptyDefinition = editedDefinition.trim() === '';
	const definitionOverMaxLength = charCount > MAX_DEFINITION_LENGTH;
	const updateDefinitionDisabled = emptyDefinition || definitionOverMaxLength;

	const [sourceSearchInput, setSourceSearchInput] = useState('');
	const [source, setSource] = useState<CuratedDefinitionSourceType | null>(null);
	const sourceSelectRef = useRef<any>(null);
	const [sourceJustRemoved, setSourceJustRemoved] = useState(false);

	useEffect(() => {
		if (sourceJustRemoved && sourceSelectRef.current && fg('a11y-fix-source-remove-focus')) {
			sourceSelectRef.current.focus();
			setSourceJustRemoved(false);
		}
	}, [sourceJustRemoved]);

	const { definitionRestrictionOptions } = curatedDefinitionScopeConfig;
	const defaultRestrictionOption = definitionRestrictionOptions[0];
	const [audienceRestriction, setAudienceRestriction] = useState<EditedDefinitionScope>(
		defaultRestrictionOption.value,
	);

	const [updateDefinitionLoading, setUpdateDefinitionLoading] = useState(false);

	const setDefinition = useCallback(
		(value: string) => {
			setEditedDefinition(value);
		},
		[setEditedDefinition],
	);

	const { searchSources } = useSearchSourceSupplier();

	const onSourceSearchInputChange = (input: string, actionMeta: InputActionMeta) => {
		if (actionMeta.action === 'input-change') {
			setSourceSearchInput(input);
		}
	};

	const onSelectSource: SelectProps<CuratedDefinitionSourceType>['onChange'] = (
		newValue,
		actionMeta,
	) => {
		if (actionMeta.action === 'select-option') {
			setSource(newValue);
		}
	};

	const onSelectRestrictionOption: SelectProps<RestrictionOptionType>['onChange'] = (
		newValue,
		actionMeta,
	) => {
		if (actionMeta.action === 'select-option') {
			setAudienceRestriction(newValue?.value || EditedDefinitionScope.ORGANIZATION);
		}
	};

	const { createDefinition } = useCreateDefinitionSupplier({
		curatedDefinitionScopeConfig,
	});
	const onUpdateDefinition = async () => {
		setUpdateDefinitionLoading(true);
		try {
			const updatedDefinition = await createDefinition({
				keyPhrase,
				editedDefinition,
				scope: audienceRestriction,
				referenceContentId: source ? source.id : null,
				referenceUrl: source ? source.url : null,
			});
			setUpdateDefinitionLoading(false);
			setSource(null);
			fireAnalyticsEvent(
				onCuratedDefinitionUpdateButtonClicked({
					commonAttributes,
					newDefinition: editedDefinition,
					restrictedScope: audienceRestriction,
					sourceSelected: !!source,
					aiDefinitionEdited: !!!answerEditor,
				}),
			);
			setData(updatedDefinition);
			setSessionItem(
				SEARCH_AI_ANSWER_CACHE_KEY,
				JSON.stringify({
					query: keyPhrase,
					additionalContext: additionalQueryContext || '',
					data: updatedDefinition,
				}),
			);
			closeEditModal();
			onSaveEditModal?.();
		} catch (error: unknown) {
			fireAnalyticsEvent(updateDefinitionErrorPayload(error, commonAttributes));
			setUpdateDefinitionLoading(false);
		}
	};

	let conditionalSublineMessage;

	switch (audienceRestriction) {
		case EditedDefinitionScope.SPACE: {
			conditionalSublineMessage = messages.edit_definition_restricted_to_space_subline;
			break;
		}
		case EditedDefinitionScope.PROJECT: {
			conditionalSublineMessage = FeatureGates.getExperimentValue(
				'project-terminology-refresh',
				'isEnabled',
				false,
			)
				? messages.edit_definition_restricted_to_project_subline_galaxia
				: messages.edit_definition_restricted_to_project_subline;
			break;
		}
		case EditedDefinitionScope.PAGE: {
			conditionalSublineMessage = messages.edit_definition_restricted_to_page_subline;
			break;
		}
		case EditedDefinitionScope.BLOGPOST: {
			conditionalSublineMessage = messages.edit_definition_restricted_to_space_subline;
			break;
		}
		case EditedDefinitionScope.ORGANIZATION: {
			conditionalSublineMessage = messages.edit_definition_restricted_to_site_subline;
			break;
		}
		default: {
			conditionalSublineMessage = messages.edit_definition_restricted_to_site_subline;
			break;
		}
	}

	return (
		<Modal onClose={closeEditModal} shouldScrollInViewport={true}>
			<ModalHeader hasCloseButton>
				<ModalTitle>
					{formatMessage(
						isCreateDefinition
							? messages.edit_definition_modal_header_create
							: messages.edit_definition_modal_header_edit,
					)}
				</ModalTitle>
			</ModalHeader>
			<ModalBody>
				<Stack space="space.150">
					<Text as="p" color="color.text.subtle">
						{formatMessage(messages.edit_definition_modal_subheader_text)}{' '}
						<Link href="https://support.atlassian.com/rovo/docs/add-or-edit-a-definition/">
							{formatMessage(messages.edit_definition_modal_subheader_link_text)}
						</Link>
					</Text>
					<Box>
						{/**
						 * Hide this text from screen readers since a label is provided as a prop to MarkdownEditor
						 * Using a Box with aria-hidden because aria-hidden gets stripped when added to Inline
						 */}
						<Box aria-hidden>
							<Inline alignBlock="center" grow="fill" spread="space-between">
								<Box xcss={editDefintionModalStyles.editDefinitionTextboxLabelContainer}>
									<Text color="color.text.subtle" weight="bold" size="small">
										{formatMessage(messages.edit_definition_text_box_label)}
									</Text>
								</Box>
								<Inline alignBlock="center" space="space.075">
									<Text as="span" size="small" color="color.text.subtle">
										{charCount}/{MAX_DEFINITION_LENGTH}
									</Text>
									<Box xcss={editDefintionModalStyles.radialProgressBarContainer}>
										<RadialProgressBar current={charCount} max={MAX_DEFINITION_LENGTH} />
									</Box>
								</Inline>
							</Inline>
						</Box>
						<MarkdownEditor
							value={editedDefinition}
							setValue={setDefinition}
							setCharCount={setCharCount}
							assistiveLabel={formatMessage(messages.edit_definition_text_box_label_with_count, {
								count: charCount,
								max: MAX_DEFINITION_LENGTH,
							})}
							assistiveDescribedBy="edit-definition-text-box-error-message"
						/>
						{updateDefinitionDisabled && (
							<Inline
								alignBlock="center"
								space="space.050"
								xcss={editDefintionModalStyles.editDefinitionTextboxErrorMessageContainer}
							>
								<ErrorIcon label="" color={token('color.text.danger')} size="small" />
								<Text
									color="color.text.danger"
									size="small"
									id="edit-definition-text-box-error-message"
								>
									{emptyDefinition
										? formatMessage(messages.edit_definition_no_definition)
										: formatMessage(messages.edit_definition_over_max_length, {
												max: MAX_DEFINITION_LENGTH,
											})}
								</Text>
							</Inline>
						)}
					</Box>
					<Box>
						<Label htmlFor="edit-definition-source-select">
							{formatMessage(
								source
									? messages.edit_definition_source_label
									: messages.edit_definition_add_source_label,
							)}
						</Label>
						{source ? (
							<SourceOption
								source={source}
								onClickSource={() => {
									window.open(source.url, '_blank')?.focus();
								}}
								canClear
								onRemoveSource={(e) => {
									e.stopPropagation();
									setSource(null);
									if (fg('a11y-fix-source-remove-focus')) {
										setSourceJustRemoved(true);
									}
								}}
							/>
						) : (
							<AsyncSelect
								ref={sourceSelectRef}
								components={{
									DropdownIndicator: () => null,
								}}
								loadOptions={debouncePromise((input: string) => searchSources(input), 500)}
								formatOptionLabel={(sourceOption: CuratedDefinitionSourceType) => (
									<SourceOption source={sourceOption} canClear={false} />
								)}
								onChange={onSelectSource}
								inputValue={sourceSearchInput}
								onInputChange={onSourceSearchInputChange}
								placeholder={formatMessage(messages.edit_definition_source_search_box_placeholder)}
								menuPosition="fixed"
								inputId="edit-definition-source-select"
							/>
						)}
					</Box>
					<Box>
						<Label htmlFor="edit-definition-restriction-select">
							{formatMessage(messages.edit_definition_restricted_to_label)}
						</Label>
						<Select
							options={definitionRestrictionOptions}
							defaultValue={defaultRestrictionOption}
							onChange={onSelectRestrictionOption}
							formatOptionLabel={restrictionOptionFormatter}
							isSearchable={false}
							menuPosition="fixed"
							inputId="edit-definition-restriction-select"
							descriptionId="edit-definition-restriction-subline"
						/>
						<Box xcss={editDefintionModalStyles.restrictionSelectSublineContainer}>
							<Text color="color.text.subtle" size="small">
								{formatMessage(conditionalSublineMessage)}
							</Text>
						</Box>
					</Box>
				</Stack>
			</ModalBody>
			<ModalFooter>
				{openFeedbackOptionsModal && (
					<Button
						appearance="subtle"
						onClick={() => {
							closeEditModal();
							openFeedbackOptionsModal();
						}}
					>
						{formatMessage(messages.edit_definition_back_button)}
					</Button>
				)}
				<Button
					appearance={updateDefinitionDisabled ? 'default' : 'primary'}
					isLoading={updateDefinitionLoading}
					onClick={onUpdateDefinition}
					isDisabled={updateDefinitionDisabled}
				>
					{formatMessage(messages.edit_definition_update_button)}
				</Button>
			</ModalFooter>
		</Modal>
	);
};
